/**
 * Portfolio, from hung-hola blocks/portfolio/view.js (animation and the owner's decisions: hung-hola
 * docs/animation/portfolio.md).
 *
 * - Background: the Banner Wall video (Figma's video fill), loaded when the section comes near and played while it is
 *   on screen, from 1281px only; below that, with ?still or reduced motion: its poster.
 * - Dot grid: the shared block dots (blockDots(), assets/js/dot-ripple.ts): a spotlight on hover, a light sweep on
 *   click, with a mouse only; the CSS dots stay on phones and tablets, with ?still, reduced motion or no WebGL2.
 * - Desktop (> 1280), the orbit of docs/prototypes/orbit-loop.html driven by the scroll: the project cards on ONE
 *   tilted plane round a circle (left side nearer and bigger, the ring leaning left), repeated to fill RING.slots
 *   (copies aria-hidden, out of the tab order). The section is one screen + the timeline's scroll (--portfolio-scroll);
 *   its stage is sticky meanwhile (no ScrollTrigger pin). Everything after the heading's shared fade-in (px:faded-in)
 *   is scrubbed by the scroll alone (never carried or input held): the intro (the cards come out of the centre and
 *   swirl out to the ring) → the CTA → the zoom to the first card's close-up → the ring turns one project per
 *   CARD_SCREENS screens, ends on the last one and the section scrolls away. In the close-up only, a stop between two
 *   projects glides to the nearest one, its card centred (SNAP_WAIT). Scrolling up plays it backwards; leaving
 *   upwards and coming back plays it all again (data-px-fade-in-replay).
 * - ≤ 1280: a Flickity slider (loop and autoplay from the fields; MOTION.slider.autoplay), shown after the heading.
 * - ?still, reduced motion, no GSAP (motionLib() null): the still ring / the slider without autoplay, nothing moves.
 * Timeline units are seconds; SCROLL_PER_SECOND turns them into scroll distance.
 */
import { blockDots, dotsPlay } from '../../assets/js/dot-ripple';
import { getFlickity } from '../../assets/js/flickity-global';
import { MOTION, isStill, motionLib } from '../../assets/js/motion';

type FlickityInstance = { destroy: () => void };
// The shared "px-flickity" script (block.json viewScript), not bundled here; without it the slider stays as without JS
const FlickitySlider = getFlickity<new (element: Element, options: Record<string, unknown>) => FlickityInstance>();

const WIDE = '(min-width: 1281px)'; // the theme's touch: is ≤ 1280 (hung-hola's xl switch)
const VIDEO_SPEED = 0.5; // the Banner Wall plays at half speed (owner)
const SLIDER = { selectedAttraction: 0.025, friction: 0.28 }; // the same as services (Flickity defaults)

// Desktop intro, in real seconds
const INTRO = {
	revealDuration: 1.2, // fade 0 -> 1 (the ring fades in softly, never pops up)
	revealEase: 'power2.out',
	spreadDuration: 1.3, // slide from the centre to the ring
	spreadEase: 'power2.out',
	stagger: 0.03, // between two cards: all at once, just not in lockstep
	// Swirl (the cards swirl out from the centre), rounded to whole slots (24 slots of 15 deg) so a card still lines up
	// at the focus in the close-up
	swirlTurn: 45,
	swirlDuration: 0.5,
	swirlEase: 'power2.out',
};
// Desktop orbit numbers (hung-hola's, tuned with its ?tune panel)
const ORBIT = {
	SCROLL_PER_SECOND: 300, // px of scroll per timeline second
	SCRUB: 0.02, // s the timeline takes to catch up with the scroll (power3.out)
	TEXT_HOLD: 0, // s at the start
	RING_HOLD: 0, // s of rest on the ring before the zoom
	// s the zoom starts before the intro ends (no stop between them); never before the swirl has put the first project
	// at the focus
	ZOOM_OVERLAP: 1.2,
	ZOOM_IN: 4.0, // the camera flies straight at the nearest card
	CARD_SCREENS: 1.5, // screen heights of scroll per project in the close-up
	// While the ring shows, before the zoom, it keeps turning slowly and a wheel spins it faster (CodeGrid's circular
	// gallery, degrees per 60 fps frame)
	IDLE_SPEED: 0.035,
	WHEEL_ACCELERATION: 0.0055, // per px of wheel delta
	MAX_SPEED: 2.5,
	SPEED_EASING: 0.05, // share of the gap back to IDLE_SPEED closed each frame
	// Close-up only (owner): the scroll stopped SNAP_WAIT s off a project's own position (its card centred) -> glide in
	// SNAP_DURATION s to the nearest one (cancelled by any new scroll input)
	SNAP_WAIT: 0.5,
	SNAP_DURATION: 0.6,
};
// Ring geometry: Figma px at 1920×1080, times u = min(innerWidth / 1920, innerHeight / 1080); angles in degrees.
// render.php and portfolio.css draw the still ring from the same numbers.
const RING = {
	slots: 24, // cards round the ring (the projects repeat)
	perspective: 820,
	plane: { rotateX: 8, rotateY: 32, rotateZ: -12 }, // left side nearer, ring leaning left
	offsetX: 24, // the nearer left side looks bigger: the ring sits a little right of centre
	radius: 200,
	card: { width: 84, height: 52.5 }, // 16:10 like the project images
	focus: 180, // deg on the ring the camera flies at: the left point, the nearest
	// x12, almost flat (half hung-hola's 2 / 10 / -4, owner: flatter, still leaning right), gaps; the card at the focus x1.12
	close: { zoom: 12, plane: { rotateX: 1, rotateY: 5, rotateZ: -2 }, card: 0.86, active: 0.12 },
	dim: 0.25, // in the close-up, black over the cards not at the focus (0 none, 1 black), less as one nears the focus
};
// The close-up's active card leans towards the mouse (mouse only): deg at the screen's edge, the link's own perspective
const TILT = { max: 16, perspective: 900, duration: 0.6, ease: 'power3.out' };
// The eye over the active card: s the circle takes to catch the mouse, s it grows in / shrinks away
const CURSOR = { follow: 0.35, show: 0.3 };
const CHROME = { out: [0.3, 1.3] }; // the heading fades out during the zoom (the CTA stays)
const DOT_GRID = { gap: 27, size: 3, inset: 27, color: [1, 1, 1, 0.3] as [number, number, number, number] };

const rad = (deg: number): number => (deg * Math.PI) / 180;
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const clamp = (min: number, max: number, value: number): number => Math.min(max, Math.max(min, value));
const wrap = (deg: number): number => ((((deg + 180) % 360) + 360) % 360) - 180;
const power3Out = (t: number): number => 1 - Math.pow(1 - t, 3);
const power2InOut = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/** The Banner Wall video: nothing downloads until the section is near; plays only on screen, tab visible, desktop. */
function background(wrapper: HTMLElement, video: HTMLVideoElement): void {
	const wide = window.matchMedia(WIDE);
	let loaded = false;
	let inView = false;
	const update = (): void => {
		if (!inView || document.hidden || !wide.matches) {
			if (!video.paused) {
				video.pause();
			}
			return;
		}
		if (!loaded) {
			video.querySelectorAll<HTMLSourceElement>('source[data-src]').forEach((source) => {
				source.src = source.dataset.src || '';
			});
			video.load();
			video.defaultPlaybackRate = VIDEO_SPEED; // load() resets playbackRate to this
			video.playbackRate = VIDEO_SPEED;
			loaded = true;
		}
		if (video.paused) {
			video.play().catch(() => {});
		}
	};
	new IntersectionObserver(
		(entries) => {
			inView = entries[entries.length - 1].isIntersecting;
			update();
		},
		{ rootMargin: '200px 0px' }
	).observe(wrapper);
	document.addEventListener('visibilitychange', update);
	wide.addEventListener('change', update);
}

/**
 * The dot grid on the stage: dotGrid() draws a fixed number of dots, so the canvas is sized to whole 27px cells from
 * the stage's top left (one inset round it, so grown dots stay whole); a stage that outgrows it gets a new canvas.
 */
function dots(stage: HTMLElement, first: HTMLCanvasElement): void {
	const { gap, size, inset, color } = DOT_GRID;
	let canvas = first;
	let cols = 0;
	let rows = 0;
	const build = (): void => {
		const needCols = Math.ceil(Math.max(stage.clientWidth, window.screen.width) / gap) + 1;
		const needRows = Math.ceil(stage.clientHeight / gap) + 1;
		if (needCols <= cols && needRows <= rows) {
			return;
		}
		cols = Math.max(cols, needCols);
		rows = Math.max(rows, needRows + 8);
		const fresh = canvas.cloneNode() as HTMLCanvasElement; // a WebGL grid keeps its count: a new canvas for a new one
		fresh.style.cssText = `left:${-inset}px;top:${-inset}px;width:${2 * inset + size + (cols - 1) * gap}px;height:${2 * inset + size + (rows - 1) * gap}px`;
		canvas.replaceWith(fresh);
		canvas = fresh;
		blockDots(fresh, stage, { cols, rows, gapY: gap, size, color, inset }, stage);
	};
	build();
	new ResizeObserver(build).observe(stage);
}

document.querySelectorAll<HTMLElement>('[data-px-block="portfolio"]').forEach((wrapper) => {
	const still = isStill();
	const lib = motionLib(); // null with ?still, reduced motion or no GSAP: nothing moves
	const animated = !!lib;
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	const video = wrapper.querySelector<HTMLVideoElement>('[data-portfolio-bg]');
	if (video && !still && 'IntersectionObserver' in window) {
		background(wrapper, video);
	}

	const stage = wrapper.querySelector<HTMLElement>('.portfolio__stage');
	const canvas = wrapper.querySelector<HTMLCanvasElement>('[data-portfolio-dots]');
	if (stage && canvas && dotsPlay() && 'ResizeObserver' in window) {
		dots(stage, canvas);
	}

	const ring = wrapper.querySelector<HTMLElement>('[data-portfolio-ring]');
	if (!ring) {
		return;
	}
	const SHOW = MOTION.reveal;

	// Order: the heading appears first (shared fade-in), only then the cards, then the CTA
	const fadeIn = wrapper.querySelector<HTMLElement>('[data-px-fade-in]');
	const cta = wrapper.querySelector<HTMLElement>('[data-portfolio-cta]');
	let textDone = !animated || !fadeIn || fadeIn.hasAttribute('data-px-faded-in');
	fadeIn?.addEventListener('px:faded-in', () => {
		textDone = true;
	});
	// Replay: the shared fade-in hides the heading again once the section is below the screen and fires
	// px:fade-in-reset; the desktop ring is back at the start by then, the slider hides and waits again
	fadeIn?.addEventListener('px:fade-in-reset', () => {
		textDone = false;
		wrapper.dispatchEvent(new CustomEvent('portfolio:reset'));
	});
	const whenText = (fn: () => void): void => {
		if (textDone || !fadeIn) {
			fn();
			return;
		}
		fadeIn.addEventListener('px:faded-in', fn, { once: true });
	};
	// The heading's fade-in, finished at once (desktop: the scroll reached the intro first)
	const finishText = (): void => {
		if (textDone || !fadeIn || !lib) {
			return;
		}
		lib.gsap.getTweensOf(fadeIn.querySelectorAll('*')).forEach((tween) => tween.progress(1));
		textDone = true;
	};
	const hideCta = (): void => {
		if (cta && lib) {
			lib.gsap.killTweensOf(cta);
			lib.gsap.set(cta, { autoAlpha: 0, y: SHOW.rise });
		}
	};
	hideCta();

	const slider = (): (() => void) => {
		if (!FlickitySlider) {
			return () => {};
		}
		let active = true;
		let armed = 0;
		const loop = ring.dataset.portfolioLoop !== '0';
		const autoplay = ring.dataset.portfolioAutoplay !== '0' && animated;
		const flickity = new FlickitySlider(ring, {
			cellAlign: loop ? 'center' : 'left', // looping: the selected card in the middle, equal peeks both sides
			contain: !loop,
			wrapAround: loop,
			autoPlay: autoplay ? MOTION.slider.autoplay : false,
			pauseAutoPlayOnHover: true,
			prevNextButtons: false,
			pageDots: false,
			percentPosition: false,
			selectedAttraction: reduceMotion ? 1 : SLIDER.selectedAttraction,
			friction: reduceMotion ? 1 : SLIDER.friction,
		});
		const arm = (): void => {
			if (!lib) {
				return;
			}
			const { gsap } = lib;
			const id = ++armed;
			gsap.killTweensOf(ring);
			gsap.set(ring, { autoAlpha: 0, y: SHOW.rise });
			hideCta();
			whenText(() => {
				if (!active || id !== armed) {
					return;
				}
				gsap.to(ring, { autoAlpha: 1, y: 0, duration: SHOW.duration, ease: SHOW.ease, clearProps: 'opacity,visibility,transform' });
				if (cta) {
					gsap.to(cta, { autoAlpha: 1, y: 0, duration: SHOW.duration, ease: SHOW.ease, delay: SHOW.stagger, clearProps: 'opacity,visibility,transform' });
				}
			});
		};
		arm();
		wrapper.addEventListener('portfolio:reset', arm);
		return () => {
			active = false;
			flickity.destroy();
			wrapper.removeEventListener('portfolio:reset', arm);
			if (lib) {
				lib.gsap.killTweensOf(ring);
				lib.gsap.set(ring, { clearProps: 'opacity,visibility,transform' });
			}
		};
	};

	const orbit = (): (() => void) => {
		// Fill the ring: card k shows project k % n; copies are decoration for assistive tech and the keyboard
		const projects = ring.children.length;
		const copies: HTMLElement[] = [];
		for (let k = projects; k < RING.slots; k++) {
			const copy = ring.children[k % projects].cloneNode(true) as HTMLElement;
			copy.setAttribute('aria-hidden', 'true');
			copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
			copy.querySelectorAll('a').forEach((a) => {
				a.setAttribute('tabindex', '-1');
				a.removeAttribute('aria-labelledby');
			});
			ring.append(copy);
			copies.push(copy);
		}
		const ringCards = [...ring.children] as HTMLElement[];
		const slot = 360 / ringCards.length;

		// Per card: intro spread (x radius) and reveal (opacity), tweened on data objects
		const introData = ringCards.map(() => ({ spread: animated ? 0 : 1, reveal: animated ? 0 : 1 }));
		// Camera / scroll state, tweened by the timeline; render() turns it into transforms.
		// zoom: log of the zoom factor; close: 0 ring -> 1 close-up; intro: 0 -> 1 over the intro; swirl: deg of the swirl;
		// align: 0 -> 1 with the swirl, turning the ring so the first project is at the focus when the ring is complete
		const state = { zoom: 0, close: 0, turn: 0, chrome: 1, intro: animated ? 0 : 1, swirl: 0, align: 0 };
		let alignBy: number | null = null; // deg align turns, fixed when it leaves 0 (the idle spin is still then)
		let idleSpin = 0;
		let activeCard: HTMLElement | null = null; // the close-up's card at the focus (is-active)
		let onActive: (() => void) | null = null;
		let u = 1;
		const measure = (): void => {
			u = Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
			ring.style.perspective = `${RING.perspective * u}px`;
			ringCards.forEach((card) => {
				card.style.width = `${RING.card.width * u}px`;
				card.style.height = `${RING.card.height * u}px`;
			});
		};
		measure();
		// Card k sits at -k slots, so turning the ring forward (bottom to top at the focus) brings the projects to the
		// focus in their own order 1, 2, 3...
		const place = (k: number): number => ((ringCards.length - k) % ringCards.length) * slot;
		// A card of the FIRST project must sit at the focus before the zoom starts: the intro's swirl carries the ring there
		const firstSpins = ringCards
			.map((card, k) => k)
			.filter((k) => k % projects === 0)
			.map((k) => RING.focus - place(k) - INTRO.swirlTurn);
		const toFirst = (from: number): number => {
			let lined = from;
			let best = Infinity;
			firstSpins.forEach((spin) => {
				const near = from + wrap(spin - from);
				if (Math.abs(near - from) < best) {
					best = Math.abs(near - from);
					lined = near;
				}
			});
			return lined;
		};
		const appearSpin = (): number => {
			if (state.align <= 0) {
				alignBy = null;
			} else if (alignBy === null) {
				alignBy = toFirst(idleSpin) - idleSpin;
			}
			const from = idleSpin + (alignBy ?? 0) * state.align;
			return lerp(from, toFirst(from), power2InOut(clamp(0, 1, state.close)));
		};
		const render = (): void => {
			const c = state.close;
			const rx = lerp(RING.plane.rotateX, RING.close.plane.rotateX, c);
			const ry = lerp(RING.plane.rotateY, RING.close.plane.rotateY, c);
			const rz = lerp(RING.plane.rotateZ, RING.close.plane.rotateZ, c);
			const P = RING.perspective * u;
			const camZ = P * (1 - 1 / Math.exp(state.zoom)); // towards the camera: x exp(zoom) on screen
			const R = RING.radius * u;
			const offX = RING.offsetX * u * (1 - c);
			// Zoom straight at the focus card: shift the plane so the focus point's screen position goes from where it is
			// on the ring straight to the centre, in step with the zoom (perspective projection solved for the shift)
			const tx = Math.cos(rad(RING.focus)) * R;
			const ty = Math.sin(rad(RING.focus)) * R;
			const X1 = tx * Math.cos(rad(ry)) + ty * Math.sin(rad(rx)) * Math.sin(rad(ry));
			const Z1 = -tx * Math.sin(rad(ry)) + ty * Math.sin(rad(rx)) * Math.cos(rad(ry));
			const X0 = tx * Math.cos(rad(RING.plane.rotateY)) + ty * Math.sin(rad(RING.plane.rotateX)) * Math.sin(rad(RING.plane.rotateY));
			const Z0 = -tx * Math.sin(rad(RING.plane.rotateY)) + ty * Math.sin(rad(RING.plane.rotateX)) * Math.cos(rad(RING.plane.rotateY));
			const screen0 = ((RING.offsetX * u + X0) * P) / (P - Z0);
			const wanted = screen0 * (1 - c);
			const D = P - camZ;
			const keep = c === 0 ? 1 : clamp(0, 1, (wanted * D - offX * P) / (X1 * P + wanted * Z1));
			const fx = tx * (1 - keep);
			const fy = ty * (1 - keep);
			const cardScale = lerp(1, RING.close.card, c);
			const spin = state.turn + state.swirl + appearSpin();
			let activeNow: HTMLElement | null = null;
			ringCards.forEach((card, k) => {
				const a = place(k) + spin;
				const d = introData[k];
				const px = Math.cos(rad(a)) * R * d.spread - fx;
				const py = Math.sin(rad(a)) * R * d.spread - fy;
				// depth of the card centre after the plane rotations: nearer cards stack on top
				const z = -px * Math.sin(rad(ry)) + py * Math.sin(rad(rx)) * Math.cos(rad(ry));
				// in the close-up, the card passing the focus grows a little, smoothly with the turn
				const near = power2InOut(clamp(0, 1, 1 - Math.abs(wrap(a - RING.focus)) / slot)); // 1 at the focus
				const active = near * c;
				const scale = cardScale * (1 + RING.close.active * active);
				card.style.transform = `translate(-50%, -50%) translate3d(${offX}px, 0, ${camZ}px) rotateZ(${rz}deg) rotateY(${ry}deg) rotateX(${rx}deg) translate3d(${px}px, ${py}px, 0) scale(${scale})`;
				card.classList.toggle('is-active', active > 0.5);
				card.style.setProperty('--card-dim', String(RING.dim * c * (1 - near)));
				if (active > 0.5) {
					activeNow = card;
				}
				card.style.opacity = String(d.reveal);
				card.style.visibility = d.reveal > 0.001 ? '' : 'hidden';
				card.style.zIndex = String(Math.round(z * 10) + 100000);
			});
			if (activeNow !== activeCard) {
				activeCard = activeNow;
				onActive?.();
			}
			// for reading the state (tests, devtools): plain numbers, not used by the CSS
			ring.style.setProperty('--ring-close', String(c));
			ring.style.setProperty('--ring-turn', String(state.turn));
			ring.classList.toggle('is-intro', state.intro < 1);
			wrapper.style.setProperty('--portfolio-chrome', String(state.chrome));
			wrapper.classList.toggle('is-orbit-inside', state.chrome < 0.01);
		};
		render();

		const cleanup = (): void => {
			window.removeEventListener('resize', measure);
			ring.classList.remove('is-intro');
			ringCards.forEach((card) =>
				['transform', 'opacity', 'visibility', 'z-index', 'width', 'height', '--card-dim'].forEach((prop) => card.style.removeProperty(prop))
			);
			copies.forEach((copy) => copy.remove());
			['--ring-close', '--ring-turn', 'perspective'].forEach((prop) => ring.style.removeProperty(prop));
			['--portfolio-chrome', '--portfolio-scroll'].forEach((prop) => wrapper.style.removeProperty(prop));
			wrapper.classList.remove('is-orbit-inside');
		};
		const onResize = (): void => {
			measure();
			render();
		};
		window.addEventListener('resize', onResize);
		if (!lib) {
			// the still ring: one screen tall, no scrub
			return () => {
				window.removeEventListener('resize', onResize);
				cleanup();
			};
		}
		const { gsap, ScrollTrigger } = lib;
		hideCta();

		const {
			SCROLL_PER_SECOND,
			SCRUB,
			TEXT_HOLD,
			RING_HOLD,
			ZOOM_OVERLAP,
			ZOOM_IN,
			CARD_SCREENS,
			IDLE_SPEED,
			WHEEL_ACCELERATION,
			MAX_SPEED,
			SPEED_EASING,
			SNAP_WAIT,
			SNAP_DURATION,
		} = ORBIT;
		// Timeline (all while the stage sticks, scrubbed by the scroll): hold -> the images come out, the zoom straight at
		// the nearest card starting before they are all in place -> the ring turns one project per CARD_SCREENS -> on the last one the section scrolls away
		const introAt = TEXT_HOLD;
		const lastCardAt = INTRO.stagger * (ringCards.length - 1);
		const introEnd = Math.max(
			lastCardAt + Math.max(INTRO.spreadDuration, INTRO.revealDuration, cta ? SHOW.duration : 0),
			INTRO.swirlDuration
		);
		const zoomAt = introAt + Math.max(INTRO.swirlDuration, introEnd - ZOOM_OVERLAP) + RING_HOLD;
		const turnAt = zoomAt + ZOOM_IN;
		const cardTime = (CARD_SCREENS * window.innerHeight) / SCROLL_PER_SECOND; // timeline s per project
		const end = turnAt + (projects - 1) * cardTime;

		// Progress round the CTA: a pill path 1px inside its border box (the stroke covers its outer 2px), from the top
		// centre clockwise; --portfolio-progress = how far the timeline is
		const progressPath = cta?.querySelector<SVGPathElement>('[data-portfolio-progress]');
		const drawProgress = (): void => {
			if (!cta || !progressPath) {
				return;
			}
			const w = cta.offsetWidth;
			const h = cta.offsetHeight;
			const r = (h - 2) / 2;
			progressPath.parentElement?.setAttribute('viewBox', `0 0 ${w} ${h}`);
			progressPath.setAttribute(
				'd',
				`M${w / 2} 1H${w - 1 - r}A${r} ${r} 0 0 1 ${w - 1 - r} ${h - 1}H${1 + r}A${r} ${r} 0 0 1 ${1 + r} 1Z`
			);
		};
		const progressObserver = new ResizeObserver(drawProgress);
		if (cta) {
			progressObserver.observe(cta);
		}
		const timeline = gsap.timeline({
			paused: true,
			defaults: { ease: 'none' },
			onUpdate: () => {
				render();
				cta?.style.setProperty('--portfolio-progress', String(timeline.progress()));
			},
		});
		// Intro: every image comes out from the centre (a little staggered) and swirls out to the ring, then the CTA
		introData.forEach((data, k) => {
			const at = introAt + k * INTRO.stagger;
			timeline.to(data, { reveal: 1, duration: INTRO.revealDuration, ease: INTRO.revealEase }, at);
			timeline.to(data, { spread: 1, duration: INTRO.spreadDuration, ease: INTRO.spreadEase }, at);
		});
		timeline
			.to(state, { intro: 1, duration: lastCardAt + INTRO.spreadDuration }, introAt)
			.to(state, { swirl: INTRO.swirlTurn, duration: INTRO.swirlDuration, ease: INTRO.swirlEase }, introAt)
			.to(state, { align: 1, duration: INTRO.swirlDuration, ease: INTRO.swirlEase }, introAt);
		if (cta) {
			timeline.fromTo(
				cta,
				{ autoAlpha: 0, y: SHOW.rise },
				{ autoAlpha: 1, y: 0, duration: SHOW.duration, ease: SHOW.ease, immediateRender: false },
				introAt + lastCardAt
			);
		}
		timeline
			.to(state, { zoom: Math.log(RING.close.zoom), duration: ZOOM_IN, ease: 'none' }, zoomAt) // on log(zoom): steady growth
			.to(state, { close: 1, duration: ZOOM_IN, ease: 'power2.inOut' }, zoomAt)
			.to(state, { chrome: 0, duration: CHROME.out[1] - CHROME.out[0] }, zoomAt + CHROME.out[0])
			// one slot per project, evenly with the scroll (no rests, no snap); going up at the left point = growing angle
			.to(state, { turn: (projects - 1) * slot, duration: end - turnAt }, turnAt);

		// Appearing spin (CodeGrid) while the ring shows, before the zoom; waits until every card is in place
		let idleSpeed = IDLE_SPEED;
		let idleDirection = 1;
		const appearing = (): boolean => state.intro >= 1 && timeline.time() <= zoomAt;
		let inView = false;
		const onWheel = (e: WheelEvent): void => {
			if (!inView || !appearing()) {
				return;
			}
			idleDirection = e.deltaY > 0 ? 1 : -1;
			idleSpeed = Math.min(idleSpeed + Math.abs(e.deltaY) * WHEEL_ACCELERATION, MAX_SPEED);
		};
		const spinIdle = (): void => {
			if (!inView || !appearing()) {
				return;
			}
			const frames = gsap.ticker.deltaRatio(60);
			idleSpeed += (IDLE_SPEED - idleSpeed) * Math.min(1, SPEED_EASING * frames);
			idleSpin += idleSpeed * idleDirection * frames;
			render();
		};
		const viewObserver = new IntersectionObserver((entries) => {
			inView = entries[entries.length - 1].isIntersecting;
		});
		viewObserver.observe(wrapper);
		window.addEventListener('wheel', onWheel, { passive: true });

		// Tilt: the active card's link turns towards the mouse, measured from the screen's centre (where the close-up
		// card sits, so nothing is measured); the card's own transform stays the ring's. Back flat when it stops being
		// the active one or the mouse leaves the page
		const pointer = { x: 0, y: 0 };
		let tilted: HTMLElement | null = null;
		const tilt = (): void => {
			const link = activeCard?.querySelector<HTMLElement>('.portfolio__link') ?? null;
			if (tilted && tilted !== link) {
				const flat = { duration: TILT.duration, ease: TILT.ease, overwrite: 'auto' as const };
				gsap.to(tilted, { rotationX: 0, rotationY: 0, ...flat });
				gsap.to(tilted.parentElement, { '--tilt-x': 0, '--tilt-y': 0, ...flat });
			}
			tilted = link;
			if (link) {
				gsap.to(link, {
					rotationX: -pointer.y * TILT.max,
					rotationY: pointer.x * TILT.max,
					transformPerspective: TILT.perspective,
					duration: TILT.duration,
					ease: TILT.ease,
					overwrite: 'auto',
				});
				// on the card: the glare (portfolio.css) moves with the tilt
				gsap.to(link.parentElement, {
					'--tilt-x': pointer.x,
					'--tilt-y': pointer.y,
					duration: TILT.duration,
					ease: TILT.ease,
					overwrite: 'auto',
				});
			}
		};
		// The eye: follows the mouse while it is over the active card (hit testing follows the 3D transforms)
		const cursor = wrapper.querySelector<HTMLElement>('[data-portfolio-cursor]');
		const cursorX = cursor ? gsap.quickTo(cursor, 'x', { duration: CURSOR.follow, ease: 'power3.out' }) : null;
		const cursorY = cursor ? gsap.quickTo(cursor, 'y', { duration: CURSOR.follow, ease: 'power3.out' }) : null;
		let cursorOn = false;
		const showCursor = (on: boolean): void => {
			if (!cursor || on === cursorOn) {
				return;
			}
			cursorOn = on;
			wrapper.classList.toggle('is-cursor', on);
			gsap.to(cursor, {
				scale: on ? 1 : 0,
				autoAlpha: on ? 1 : 0,
				duration: CURSOR.show,
				ease: on ? 'back.out(1.7)' : 'power2.in',
				overwrite: 'auto',
			});
		};
		let mouseIn = false;
		// Glare: shown on the card under the eye; it moves with the tilt (--tilt-x / -y in tilt())
		let glared: HTMLElement | null = null;
		const glare = (): void => {
			const link = cursorOn ? (activeCard?.querySelector<HTMLElement>('.portfolio__link') ?? null) : null;
			if (glared && glared !== link) {
				glared.classList.remove('is-glare');
			}
			glared = link;
			if (!link) {
				return;
			}
			link.classList.add('is-glare');
		};
		const updateCursor = (): void => {
			showCursor(mouseIn && inView && Boolean(activeCard?.matches(':hover')));
			glare();
		};
		const onPointerMove = (e: PointerEvent): void => {
			if (e.pointerType !== 'mouse' || !inView) {
				showCursor(false);
				glare();
				return;
			}
			mouseIn = true;
			pointer.x = clamp(-1, 1, (e.clientX / window.innerWidth) * 2 - 1);
			pointer.y = clamp(-1, 1, (e.clientY / window.innerHeight) * 2 - 1);
			tilt();
			if (cursor && !cursorOn) {
				gsap.set(cursor, { x: e.clientX, y: e.clientY }); // appears at the mouse, never flies in
			}
			cursorX?.(e.clientX);
			cursorY?.(e.clientY);
			updateCursor();
		};
		const onPointerLeave = (): void => {
			mouseIn = false;
			pointer.x = 0;
			pointer.y = 0;
			tilt();
			showCursor(false);
			glare();
		};
		onActive = (): void => {
			tilt();
			updateCursor();
		};
		window.addEventListener('pointermove', onPointerMove, { passive: true });
		document.documentElement.addEventListener('pointerleave', onPointerLeave);
		gsap.ticker.add(spinIdle);

		// Decode the big card images before the zoom needs them (a first decode mid-scrub drops frames)
		const predecode = ScrollTrigger.create({
			trigger: wrapper,
			start: 'top bottom+=100%',
			once: true,
			onEnter: () =>
				ring.querySelectorAll('img').forEach((img) => {
					img.loading = 'eager';
					img.decode().catch(() => {});
				}),
		});

		// Scroll mapping: the whole timeline plays while the stage sticks (the section's extra height is exactly that)
		wrapper.style.setProperty('--portfolio-scroll', `${end * SCROLL_PER_SECOND}px`);
		let holdPart = 0;
		const sync = (): void => {
			const time = holdPart * end;
			if (time > introAt) {
				finishText(); // the heading first, whatever the scroll speed
			}
			gsap.to(timeline, { time, duration: SCRUB, ease: 'power3.out', overwrite: true });
		};
		// Snap (close-up only): once the scroll has stopped, the nearest project's whole position (its card centred).
		// Lenis carries the page (a window.scrollTo would be taken back by its next frame)
		let snapCall: gsap.core.Tween | null = null;
		let snapTween: gsap.core.Tween | null = null;
		const snap = (): void => {
			const time = holdPart * end;
			// Never once the section is left: its last update (progress 1) would pull the page back from the next one
			if (!scrub.isActive || time < turnAt || time > end) {
				return;
			}
			const k = clamp(0, projects - 1, Math.round((time - turnAt) / cardTime));
			const to = scrub.start + ((turnAt + k * cardTime) / end) * (scrub.end - scrub.start);
			if (Math.abs(to - window.scrollY) < 2) {
				return;
			}
			if (window.lenis) {
				window.lenis.scrollTo(to, { duration: SNAP_DURATION, easing: power3Out });
				return;
			}
			const page = { y: window.scrollY };
			snapTween = gsap.to(page, {
				y: to,
				duration: SNAP_DURATION,
				ease: 'power3.out',
				onUpdate: () => window.scrollTo({ top: page.y, behavior: 'instant' as ScrollBehavior }),
			});
		};
		const stopSnap = (): void => {
			snapTween?.kill();
			snapTween = null;
		};
		window.addEventListener('wheel', stopSnap, { passive: true });
		window.addEventListener('touchstart', stopSnap, { passive: true });
		window.addEventListener('keydown', stopSnap);
		const scrub = ScrollTrigger.create({
			trigger: wrapper,
			start: 'top top',
			end: 'bottom bottom',
			invalidateOnRefresh: true,
			onUpdate: (self) => {
				holdPart = self.progress;
				sync();
				snapCall?.kill();
				snapCall = gsap.delayedCall(SNAP_WAIT, snap);
			},
			onLeave: () => snapCall?.kill(),
			onRefresh: (self) => {
				holdPart = self.progress;
				sync();
			},
			// Leaving upwards: back at the start at once (the images gathered at the centre, hidden)
			onLeaveBack: () => {
				snapCall?.kill();
				holdPart = 0;
				gsap.killTweensOf(timeline);
				timeline.time(0);
				render();
			},
		});

		ScrollTrigger.refresh(); // the section just grew: re-measure every trigger further down

		return () => {
			snapCall?.kill();
			stopSnap();
			window.removeEventListener('wheel', stopSnap);
			window.removeEventListener('touchstart', stopSnap);
			window.removeEventListener('keydown', stopSnap);
			predecode.kill();
			viewObserver.disconnect();
			window.removeEventListener('wheel', onWheel);
			window.removeEventListener('pointermove', onPointerMove);
			document.documentElement.removeEventListener('pointerleave', onPointerLeave);
			onActive = null;
			showCursor(false);
			if (cursor) {
				gsap.killTweensOf(cursor);
				gsap.set(cursor, { clearProps: 'all' });
			}
			const links = ring.querySelectorAll<HTMLElement>('.portfolio__link');
			gsap.killTweensOf(links);
			gsap.killTweensOf(ringCards);
			gsap.set(links, { clearProps: 'transform' });
			links.forEach((link) => {
				link.classList.remove('is-glare');
				link.parentElement?.style.removeProperty('--tilt-x');
				link.parentElement?.style.removeProperty('--tilt-y');
			});
			window.removeEventListener('resize', onResize);
			gsap.ticker.remove(spinIdle);
			scrub.kill();
			gsap.killTweensOf(timeline);
			timeline.kill();
			progressObserver.disconnect();
			cta?.style.removeProperty('--portfolio-progress');
			if (cta) {
				gsap.killTweensOf(cta);
				gsap.set(cta, { clearProps: 'opacity,visibility,transform' });
			}
			cleanup();
			ScrollTrigger.refresh();
		};
	};

	const wide = window.matchMedia(WIDE);
	let revert: (() => void) | null = null;
	const setup = (): void => {
		revert?.();
		revert = wide.matches ? orbit() : slider();
	};
	wide.addEventListener('change', setup);
	setup();
});
